import React, { useState, useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useLazyQuery } from "@apollo/client";
import { Table, InputGroup, Form } from "react-bootstrap";
import SearchIcon from "@mui/icons-material/Search";
import moment from "moment";

// Components
import CustomButton from "../../components/CustomButton";
import AnimationLoading from "../../components/Animationloading";

// Constants and Queries
import { LINEAR_GRADIENT_BG } from "../../const";
import { GET_SUVIVALS } from "../../apollo/unit/Query";
import { GET_PURCHASE_ORDER_DETAIL } from "../../apollo/purchaseOrderDetail/query";

export default function PurchaseOrderList({ tab }) {
  const history = useHistory();
  
  // Apollo queries
  const [getPurchaseOrderDetail, { data: purchaseOrderDetailData, loading }] = 
    useLazyQuery(GET_PURCHASE_ORDER_DETAIL, { fetchPolicy: "cache-and-network"});
  const [getSurvival, { loading: loadingSurvival, data: survivalData }] = 
    useLazyQuery(GET_SUVIVALS, { fetchPolicy: "cache-first" });
  
  // State
  const [searchText, setSearchText] = useState("");
  const [searchLot, setSearchLot] = useState("ALL");

  // Memoized values
  const lots = useMemo(() => 
    survivalData?.survivals?.data || [], 
  [survivalData]);
  
  const data = useMemo(() => 
    purchaseOrderDetailData?.purchaseorderdetails?.data || [], 
  [purchaseOrderDetailData]);

  // Initial data fetch
  useEffect(() => {
    if (tab !== "listOrder") return;
    
    const controller = new AbortController();
    
    const fetchData = async () => {
      try {
        await Promise.all([
          handleFetch(controller.signal),
          handleLot(controller.signal)
        ]);
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Fetch error:', error);
        }
      }
    };

    fetchData();
    return () => controller.abort();
  }, [tab]);

  // Search filter effect
  useEffect(() => {
    const controller = new AbortController();
    if (controller.signal.aborted) return;
    
    if (searchText || searchLot) {
      handleFetch(controller.signal);
    }
    
    return () => controller.abort();
  }, [searchText, searchLot]);

  // Fetch purchase order data with filters
  const handleFetch = async (signal) => {
    try {
      if (signal.aborted || loading) return;
      
      const userData = localStorage.getItem("SHOP_DATA");
      const locals = JSON.parse(userData);
      if (!locals) return;
      
      const where = {
        status: "PENDING",
        isSale: false,
        shop: locals?.id,
      };
      
      // Apply filters
      if (searchLot !== "ALL") {
        where.lot = searchLot;
      }
      
      if (searchText !== "") {
        where.search = searchText;
      }
      
      await getPurchaseOrderDetail({
        variables: {
          orderBy: "createdAt_DESC",
          where
        },
        context: { fetchOptions: { signal } },
      });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching purchase orders:", error);
      }
    }
  };

  // Fetch lot data
  const handleLot = async (signal) => {
    try {
      if (signal.aborted || loadingSurvival) return;
      
      const userData = localStorage.getItem("SHOP_DATA");
      const locals = JSON.parse(userData);
      if (!locals) return;
      
      await getSurvival({
        variables: {
          where: {
            shop: locals?.id || "",
            status: true,
            isDeleted: false
          },
        },
        context: { fetchOptions: { signal } },
      });
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error("Error fetching lots:", error);
      }
    }
  };

  // Helper function to map status to styled text
  const mapStatus = (status) => {
    switch (status) {
      case "PENDING":
        return <span style={{ color: "#3C169B" }}>ລໍຖ້າກວດສອບ</span>;
      case "COMPLETED":
        return <span style={{ color: "#28a745" }}>ອະນຸມັດແລ້ວ</span>;
      case "CANCELLED":
        return <span style={{ color: "#dc3545" }}>ປະຕິເສດ</span>;
      default:
        return <span>{status}</span>;
    }
  };

  // Show loading state
  if (loading) return <AnimationLoading />;
  
  return (
    <div className="purchaseorderlist-main">
      <div className="purchaseorders-headers" style={{ marginTop: "1em" }}>
        <div style={{ width: "100%" }}>
          <InputGroup>
            <InputGroup.Text
              style={{
                backgroundColor: "#FFFFFF",
              }}>
              <SearchIcon sx={{ color: "#6B7280" }} />
            </InputGroup.Text>
            <Form.Control
              placeholder="ຄົ້ນຫາສິນຄ້າ..."
              aria-describedby="basic-addon2"
              onChange={(e) => {
                setSearchText(e.target.value);
              }}
              value={searchText}
              style={{
                borderLeft: "none",
              }}
            />
          </InputGroup>
        </div>
        <div style={{ width: "25%", paddingLeft: "10px" }}>
          <Form.Select
            style={{
              height: "40px",
              color: "#6B7280",
              cursor: "pointer",
            }}
            onChange={(e) => {
              setSearchLot(e.target.value);
            }}
            value={searchLot}>
            {loadingSurvival ? (
              <p>Loading...</p>
            ): (
              <>
                <option value="ALL">ລ໊ອດທັງໝົດ</option>
                {lots?.map((lot) => (
                  <option key={lot.id} value={lot?.id}>
                    {lot?.survivalNumber}
                  </option>
                ))}
              </>
            )}
          </Form.Select>
        </div>
      </div>
      <div className="purchaseorderlist-content">
        <Table 
          style={{
            marginTop: "20px",
            borderCollapse: "collapse",
            width: "100% ",
            background:'#fff'
          }}>
          <thead
            style={{
              background:LINEAR_GRADIENT_BG,
              borderRadius: " 8px 8px 0px 0px",
              color: "#FFFFFF",
            }}>
            <tr>
              <th>ລຳດັບ</th>
              <th>ລ໊ອດ</th>
              <th>ເລກທີບິນ</th>
              <th>ຈຳນວນ</th>
              <th>ສະຖານະ</th>
              <th>ວັນທີ, ເດືອນ, ປີ</th>
              <th>
                ຈັດການ
              </th>
            </tr>
          </thead>
          <tbody>
            {data?.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ textAlign: "center" }}>
                  ບໍ່ມີຂໍ້ມູນ
                </td>
              </tr>
            ) : (
              data?.map((purchaseOrder, index) => (
                <React.Fragment key={`purchase-order-${index}`}>
                  <tr
                    style={{
                      alignItems: "center",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      history.push("/purchase_order_detail", {
                        state: purchaseOrder,
                      })
                    }>
                    <td style={{ paddingTop: "1rem" }}>{index + 1}</td>
                    <td style={{ paddingTop: "1rem" }}>{purchaseOrder?.lot?.survivalNumber || "-"}</td>
                    <td style={{ paddingTop: "1rem" }}>{purchaseOrder?.code}</td>
                    <td style={{ paddingTop: "1rem" }}>
                      {purchaseOrder?.amount}
                    </td>
                    <td style={{ paddingTop: "1rem" }}>
                      {mapStatus(purchaseOrder?.status)}
                    </td>
                    <td style={{ paddingTop: "1rem" }}>
                      {moment(purchaseOrder?.createdAt).format("DD/MM/YYYY HH:mm:ss")}
                    </td>
                    <td align="right">
                      <CustomButton
                        width="125px"
                        height="40px"
                        margin="0"
                        title="ກວດສອບ"
                        borderRadius="8px"
                        background="#1F3A60"
                        color="white"
                      />
                    </td>
                  </tr>
                </React.Fragment>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
}
